/**
 * Quarantine Queue Component
 * TASK-361: Document Management & Compliance System
 *
 * Displays list of documents pending review from the public upload portal.
 * Allows RMs to accept or reject uploaded documents.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield,
  RefreshCw,
  Loader2,
  AlertCircle,
  Filter,
  Search,
  Inbox,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  QuarantineDocument,
  DocType,
} from '../../types/documentCompliance';
import { secureUploadLinkService } from '../../services/secureUploadLinkService';
import QuarantineReviewCard from './QuarantineReviewCard';
import { toast } from 'sonner';

interface QuarantineQueueProps {
  clientId?: string;
  onDocumentAccepted?: () => void;
  onDocumentRejected?: () => void;
}

const QuarantineQueue: React.FC<QuarantineQueueProps> = ({
  clientId,
  onDocumentAccepted,
  onDocumentRejected,
}) => {
  const [documents, setDocuments] = useState<QuarantineDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [searchQuery, setSearchQuery] = useState('');

  // Preview state
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await secureUploadLinkService.listQuarantineDocuments({
        client_id: clientId,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });

      setDocuments(response.documents);
      setPendingCount(response.pending_count);
    } catch (err) {
      console.error('Failed to fetch quarantine documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [clientId, statusFilter]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle document preview
  const handlePreview = async (docId: string) => {
    try {
      const data = await secureUploadLinkService.getQuarantinePresignedUrl(docId);
      setPreviewUrl(data.url);
      setPreviewDocId(docId);
    } catch (err) {
      console.error('Failed to get presigned URL:', err);
      toast.error('Failed to load document preview');
    }
  };

  // Handle accept
  const handleAccept = async (docId: string, docType: DocType, expiresAt?: string) => {
    try {
      const result = await secureUploadLinkService.acceptDocument(docId, {
        doc_type: docType,
        expires_at: expiresAt,
      });
      // TASK-817: accepting kicks off an extraction that runs for tens of seconds after
      // this returns. "Document accepted successfully" was the last thing the UI ever said
      // about it — the card then vanished from this queue and the work became invisible.
      if (result.extraction_status === 'processing') {
        toast.success('Accepted — reading the document now', {
          description: 'Extracted fields appear in the client\u2019s KYC record when it finishes.',
        });
      } else {
        toast.success('Document accepted successfully');
      }
      fetchDocuments();
      onDocumentAccepted?.();
    } catch (err) {
      console.error('Failed to accept document:', err);
      toast.error('Failed to accept document');
      throw err;
    }
  };

  // Handle reject
  const handleReject = async (docId: string, reason: string, allowReupload?: boolean) => {
    try {
      await secureUploadLinkService.rejectDocument(docId, {
        reason,
        allow_reupload: allowReupload,
      });
      toast.success('Document rejected');
      fetchDocuments();
      onDocumentRejected?.();
    } catch (err) {
      console.error('Failed to reject document:', err);
      toast.error('Failed to reject document');
      throw err;
    }
  };

  // Filter documents by search
  const filteredDocuments = useMemo(() =>
    documents.filter((doc) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        doc.original_filename.toLowerCase().includes(query) ||
        doc.client_name?.toLowerCase().includes(query)
      );
    }), [documents, searchQuery]);

  // Loading state
  if (loading && documents.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#002B45] animate-spin mr-2" />
          <span className="text-gray-600">Loading quarantine queue...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex flex-col items-center justify-center text-red-600">
          <AlertCircle className="w-8 h-8 mb-2" />
          <span>{error}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDocuments}
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#002B45]" />
          <h3 className="text-lg font-medium text-gray-900">Quarantine Review</h3>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchDocuments}
          disabled={loading}
          className="flex items-center gap-1.5"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by filename or client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
            >
              <option value="pending">Pending Review</option>
              <option value="clean">Scan Complete</option>
              <option value="scanning">Scanning</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="all">All Documents</option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-lg font-medium text-gray-900 mb-1">No documents to review</h4>
          <p className="text-sm text-gray-500">
            {statusFilter === 'pending'
              ? 'Documents uploaded by clients will appear here for review.'
              : 'No documents match the current filter.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocuments.map((doc) => (
            <QuarantineReviewCard
              key={doc.id}
              document={doc}
              onAccept={handleAccept}
              onReject={handleReject}
              onPreview={handlePreview}
            />
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewDocId && previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="font-medium text-gray-900">Document Preview</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPreviewDocId(null);
                  setPreviewUrl(null);
                }}
              >
                Close
              </Button>
            </div>
            <div className="h-[80vh] overflow-auto">
              <iframe
                src={previewUrl}
                className="w-full h-full"
                title="Document Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuarantineQueue;
